import { FocusEvent, KeyboardEvent, MouseEvent } from 'react';

import { ENTER_KEY, ESCAPE_KEY, TAB_KEY } from 'core/constants';

export const isEnterKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
  return event.key === ENTER_KEY;
};

// Shift+Enter also satisfies isEnterKeyPress (deliberately — preventEnter and the part-number
// handler must keep swallowing both), so a caller that treats the two differently has to test
// this one first.
export const isShiftEnterKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
  return isEnterKeyPress(event) && event.shiftKey;
};

export const isTabKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
  return event.key === TAB_KEY;
};

export const isEscapeKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
  return event.key === ESCAPE_KEY;
};

// Submit buttons that act on mousedown must not take the focus: the validation settles in the
// microtasks right after the handler, before the browser's own "focus the pressed button", so the
// button would take the focus from the invalid field the form has just focused. Kept off the
// button, the focus stays where the keyboard path (Enter in a field) leaves it.
export const keepFocusOnMouseDown = (event?: MouseEvent<HTMLElement>) => {
  event?.preventDefault();
};

export const SUPPRESS_BLUR_PREVIEW_ATTR = 'data-suppress-blur-preview';

// Buttons don't reliably receive focus on click, so the blur fired by pressing Save can
// carry relatedTarget=null/unrelated and a pure relatedTarget check misses it. Worse, when
// the field KEEPS focus through the press, its blur only fires during the drawer teardown
// after the save round-trip completes — seconds after the mousedown — so a fixed time
// window can't cover it either. Save controls therefore mark submit intent on mousedown
// (which always precedes any of those blurs) and the intent stays LATCHED until the user
// focuses something again (editing resumed → previews re-armed) or a safety timeout.
const SUBMIT_INTENT_SAFETY_MS = 30000;
let submitIntentActive = false;
let submitIntentSafetyTimer: ReturnType<typeof setTimeout> | null = null;

// Also called when a submit bails without transitioning (see goToNextStep) — the latch would
// otherwise stay armed and eat the next unrelated blur's preview.
export const clearSubmitIntent = () => {
  submitIntentActive = false;

  if (submitIntentSafetyTimer) {
    clearTimeout(submitIntentSafetyTimer);
    submitIntentSafetyTimer = null;
  }

  document.removeEventListener('focusin', clearSubmitIntent, true);
};

export const markSubmitIntent = () => {
  clearSubmitIntent();

  submitIntentActive = true;
  // Blur fires before the new target's focusin, so a suppressed teardown blur is always
  // checked before the latch clears — even in browsers that do focus buttons on click.
  document.addEventListener('focusin', clearSubmitIntent, true);
  submitIntentSafetyTimer = setTimeout(clearSubmitIntent, SUBMIT_INTENT_SAFETY_MS);
};

// True when a blur is caused by activating a Save/submit control — either submit intent was
// just marked (mousedown on a Save control) or focus landed on an element tagged with
// SUPPRESS_BLUR_PREVIEW_ATTR. Callers use it to skip an on-blur pricing preview that the
// submit path would recompute authoritatively. Matching only tagged controls — not any
// <button> — keeps legitimate previews firing when focus moves to a Joy Select trigger,
// icon button, or autocomplete indicator. Safe default: any ambiguity (no intent, no
// relatedTarget) returns false so the normal blur preview fires.
export const isSubmitButtonBlur = (event?: FocusEvent<HTMLElement>): boolean => {
  const relatedTarget = event?.relatedTarget;

  return (
    submitIntentActive ||
    (relatedTarget instanceof HTMLElement &&
      Boolean(relatedTarget.closest(`[${SUPPRESS_BLUR_PREVIEW_ATTR}]`)))
  );
};
